import { memo, useCallback, useState } from 'react';

import { useTrackRestoreLibrary } from '@air/analytics';
import { type Library } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { useLibraryRestorationTask } from '../../hooks/useLibraryRestorationTask';
import { RecentlyDeletedRecoverLibraryConflictModal } from './RecentlyDeletedLibraryConflictModal';

export type RecentlyDeletedRecoverLibraryModalProps = {
  id: Library['id'];
  title: string;
  hasDuplicateTitle: boolean;
};

export const RecentlyDeletedRecoverLibraryModal = memo(
  ({ id, title, onClose, hasDuplicateTitle }: AirModalProps<RecentlyDeletedRecoverLibraryModalProps>) => {
    const [showModal] = useAirModal(RecentlyDeletedRecoverLibraryConflictModal);
    const { createLibraryRestorationTask } = useLibraryRestorationTask();
    const { trackRestoreLibrary } = useTrackRestoreLibrary();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToasts();

    const handleCreateTask = useCallback(
      async (newTitle?: string) => {
        setLoading(true);
        const result = await createLibraryRestorationTask({
          libraryId: id,
          title: newTitle,
        });
        if (result.data) {
          trackRestoreLibrary({
            library_id: id,
            library_name: title,
          });
          onClose();
        } else if (result.error) {
          showToast(result.error.message || 'There was an error restoring the library.');
        }
        setLoading(false);
      },
      [createLibraryRestorationTask, id, onClose, showToast, title, trackRestoreLibrary],
    );

    const handleConfirm = useCallback(() => {
      if (hasDuplicateTitle) {
        showModal({
          onSubmit: handleCreateTask,
          title,
        });
      } else {
        handleCreateTask(title);
      }
      onClose();
    }, [handleCreateTask, hasDuplicateTitle, onClose, showModal, title]);

    return (
      <ConfirmationModal
        ctaPrimaryText="Recover"
        isOpen
        onDismiss={onClose}
        onConfirm={handleConfirm}
        description={`Recovering “${title}” will also restore any boards, sub-boards and assets that were deleted within it.`}
        id="recently-deleted-recover-library"
        title="Recover library?"
        isLoading={loading}
        isDisabled={loading}
      />
    );
  },
);

RecentlyDeletedRecoverLibraryModal.displayName = 'RecentlyDeletedRecoverLibraryModal';
